import styled, { css } from 'styled-components';

import colors from 'app/styles/theme/colors';
import { StyledDialog } from 'app/components/StyledDialog';
import { dialogPaper } from 'app/components/DialogFrame/styles';

const { gray, green, blue } = colors.designSystem;

const STATE_OVERLAY = 'rgba(0, 0, 0, 0.6)';
const STATE_HOVER = 'rgba(0, 0, 0, 0.08)';

const SMALL_SCREEN = '480px';

const focusRing = css`
  &:focus-visible {
    outline: 2px solid ${blue[500]};
    outline-offset: 2px;
  }
`;

const hoverOverlay = css`
  &:hover:not(:disabled) {
    background-image: linear-gradient(${STATE_HOVER}, ${STATE_HOVER});
  }
`;

export const Dialog = styled(StyledDialog)`
  && .MuiBackdrop-root {
    background: ${STATE_OVERLAY} !important;
  }

  && .MuiDialog-paper {
    ${dialogPaper}
    position: relative;
    box-sizing: border-box;
    width: 600px;
    max-width: calc(100% - 32px);
    margin: 16px;
    padding: 40px;
    background: ${gray[0]};
    overflow-y: auto;
  }
`;

export const CloseButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: 5px;
  background: transparent;
  color: ${gray[700]};
  cursor: pointer;

  &:hover {
    background: ${STATE_HOVER};
  }

  ${focusRing}
`;

export const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 32px;
  margin: 0;
`;

export const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

export const Texts = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const Title = styled.h2`
  margin: 0;
  font-family: 'Noto Sans', sans-serif;
  font-size: 20px;
  font-weight: 600;
  line-height: 110%;
  color: ${gray[950]};
`;

export const Description = styled.p`
  margin: 0;
  font-family: 'Noto Sans', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 140%;
  color: ${gray[700]};
`;

export const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 16px;

  @media (max-width: ${SMALL_SCREEN}) {
    & > button {
      flex: 1;
    }
  }
`;

const actionBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  height: 40px;
  padding: 0 24px;
  border-radius: 5px;
  font-family: 'Noto Sans', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
  }

  ${hoverOverlay}
  ${focusRing}
`;

export const PrimaryAction = styled.button`
  ${actionBase}
  border: none;
  background-color: ${green[500]};
  color: ${gray[0]};

  &:disabled {
    background-color: ${gray[500]};
    color: ${gray[200]};
  }
`;

export const SecondaryAction = styled.button`
  ${actionBase}
  border: 1px solid ${gray[400]};
  background-color: ${gray[0]};
  color: ${gray[950]};

  &:disabled {
    border-color: ${gray[250]};
    background-color: ${gray[100]};
    color: ${gray[500]};
  }
`;
